import React from 'react';
import { NavLink } from 'react-router-dom';
import { ArrowRight, BookOpen, Server, TerminalSquare, Play } from 'lucide-react';
import { interviewQuestions } from '../data/interviewQuestions';
import { concepts } from '../data/concepts';
import { technologies } from '../data/technologies';
import SEO, { seoConfigs } from '../../components/SEO';

// Curated popular questions
const POPULAR_IDS = [
    'design-url-shortener',
    'design-chat-app',
    'design-youtube',
    'design-uber',
];

const SysDesignHome = () => {
    const popularQuestions = POPULAR_IDS
        .map(id => interviewQuestions.find(q => q.id === id))
        .filter(Boolean);

    return (
        <>
            <SEO {...seoConfigs.sysdesignHome} />

            <div className="sd-home sd-fade-in">
                {/* ── Redesigned Hero Hub ─────────────────────── */}
                <section className="sd-hero-hub sd-container">
                    <div className="sd-badge sd-mb-2" style={{ margin: '0 auto' }}>Free · No Paywall · No Signup</div>
                    <h1 className="sd-hero-title" style={{ textAlign: 'center' }}>
                        System Design<br />
                        <span className="sd-text-gradient">Masterclass.</span>
                    </h1>
                    <p className="sd-hero-subtitle" style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto 3rem auto' }}>
                        Everything you need to architect scalable systems and crush your interviews.
                    </p>

                    <div className="sd-action-cards">
                        <NavLink to="/sysdesign/concepts" className="sd-action-card">
                            <div className="sd-action-icon-wrapper" style={{ color: '#0ea5e9', background: 'rgba(14, 165, 233, 0.1)' }}>
                                <BookOpen size={28} />
                            </div>
                            <h3>Learn Core Concepts</h3>
                            <p>Master CAP theorem, caching, sharding, and the math behind the tech.</p>
                            <span className="sd-action-link">Start Learning <ArrowRight size={16} /></span>
                        </NavLink>

                        <NavLink to="/sysdesign/technologies" className="sd-action-card">
                            <div className="sd-action-icon-wrapper" style={{ color: '#8b5cf6', background: 'rgba(139, 92, 246, 0.1)' }}>
                                <Server size={28} />
                            </div>
                            <h3>Key Technologies</h3>
                            <p>Deep dives into systems like Kafka, Redis, Cassandra, and Kubernetes.</p>
                            <span className="sd-action-link">Explore Systems <ArrowRight size={16} /></span>
                        </NavLink>

                        <NavLink to="/sysdesign/interview" className="sd-action-card sd-action-primary">
                            <div className="sd-action-icon-wrapper" style={{ color: '#34d399', background: 'rgba(52, 211, 153, 0.1)' }}>
                                <TerminalSquare size={28} />
                            </div>
                            <h3>Interview Questions</h3>
                            <p>Detailed architecture solutions for YouTube, Uber, URL Shortener, and more.</p>
                            <span className="sd-action-link">Start Practicing <ArrowRight size={16} /></span>
                        </NavLink>
                    </div>
                </section>

                {/* ── Popular Questions Roster ────────────────── */}
                <section className="sd-container" style={{ marginTop: '4rem' }}>
                    <div className="sd-section-header" style={{ alignItems: 'flex-start', textAlign: 'left' }}>
                        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Play size={24} style={{ color: 'var(--sd-accent)' }} />
                            Top Practice Questions
                        </h2>
                        <p>The system design interviews companies ask the most. Start here.</p>
                    </div>

                    <div className="sd-question-roster">
                        {popularQuestions.map((q, i) => (
                            <NavLink
                                key={q.id}
                                to={`/sysdesign/interview/${q.id}`}
                                className="sd-roster-row"
                            >
                                <span className="sd-roster-num">{String(i + 1).padStart(2, '0')}</span>
                                <div className="sd-roster-content">
                                    <span className="sd-roster-title">{q.title}</span>
                                    <span className="sd-roster-desc" style={{ color: 'var(--sd-text-subtle)', fontSize: '0.9rem', display: 'block', marginTop: '4px' }}>
                                        {q.summary.split('.')[0]}.
                                    </span>
                                </div>
                                <span className="sd-roster-badge">
                                    <span className={`sd-difficulty-badge ${q.difficulty.toLowerCase()}`}>
                                        {q.difficulty}
                                    </span>
                                </span>
                                <ArrowRight size={18} className="sd-roster-arrow" />
                            </NavLink>
                        ))}
                    </div>

                    <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                        <NavLink to="/sysdesign/interview" className="sd-btn sd-btn-outline">
                            View All {interviewQuestions.length} Questions <ArrowRight size={16} />
                        </NavLink>
                    </div>
                </section>

                <section className="sd-container" style={{ textAlign: 'center', padding: '4rem 0', color: 'var(--sd-text-subtle)' }}>
                    <p>Designed for engineers, by engineers.</p>
                </section>
            </div>
        </>
    );
};

export default SysDesignHome;
